// EmergencyGlideButton.tsx — Floating "Emergency Glide" button on the map.
//
// Tap behavior (enters emergency mode):
//   1. Sets `glideRingEmergencyMode = true` in uiStore.
//   2. GlideRingLayer responds by re-zooming the map to the ring extent,
//      brightening the polygon, and adding a faint green fill.
//   3. This component fetches airports inside the ring bbox, filters to
//      those geometrically inside the polygon, and pushes them as a
//      highlight layer.
//   4. If zero airports inside, shows a red alert banner.
//
// Tap again to dismiss.
//
// Visible only when the glide-ring layer toggle is ON.

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useGpsStore } from '../stores/gpsStore';
import { useAircraftStore, selectActiveAircraft } from '../stores/aircraftStore';
import { useOwnshipWindsStore } from '../stores/ownshipWindsStore';
import { computeGlideRing, ringBbox, pointInRing } from '../lib/glideRing';
import { sampleElevationFt } from '../lib/terrainSampler';
import { fetchBbox, type BboxAirport } from '../lib/mapBbox';
import { getAirportDetail, type Runway } from '../lib/airports';
import { formatNm, bearingDeg } from '../lib/geo';
import { getMapInstance } from './MapView';
import type { Map as MlMap, GeoJSONSource, MapMouseEvent } from 'maplibre-gl';

const HIGHLIGHT_SOURCE_ID = 'glide-ring-airports';
const HIGHLIGHT_LAYER_ID = 'glide-ring-airports-highlight';
const HIGHLIGHT_LABEL_LAYER_ID = 'glide-ring-airports-label';

const FT_PER_M = 3.28084;

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function ensureHighlightLayers(map: MlMap): void {
  if (!map.getSource(HIGHLIGHT_SOURCE_ID)) {
    map.addSource(HIGHLIGHT_SOURCE_ID, { type: 'geojson', data: emptyFC() });
  }
  if (!map.getLayer(HIGHLIGHT_LAYER_ID)) {
    map.addLayer({
      id: HIGHLIGHT_LAYER_ID,
      type: 'circle',
      source: HIGHLIGHT_SOURCE_ID,
      paint: {
        'circle-radius': 12,
        'circle-color': '#fde047', // yellow-300
        'circle-opacity': 0.6,
        'circle-stroke-color': '#facc15', // yellow-400
        'circle-stroke-width': 2.5,
      },
    });
  }
  if (!map.getLayer(HIGHLIGHT_LABEL_LAYER_ID)) {
    map.addLayer({
      id: HIGHLIGHT_LABEL_LAYER_ID,
      type: 'symbol',
      source: HIGHLIGHT_SOURCE_ID,
      layout: {
        'text-field': ['get', 'ident'],
        'text-size': 12,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
      },
      paint: {
        'text-color': '#1f2937', // gray-800
        'text-halo-color': '#fde047',
        'text-halo-width': 2,
      },
    });
  }
}

function removeHighlightLayers(map: MlMap): void {
  if (map.getLayer(HIGHLIGHT_LABEL_LAYER_ID)) map.removeLayer(HIGHLIGHT_LABEL_LAYER_ID);
  if (map.getLayer(HIGHLIGHT_LAYER_ID)) map.removeLayer(HIGHLIGHT_LAYER_ID);
  if (map.getSource(HIGHLIGHT_SOURCE_ID)) map.removeSource(HIGHLIGHT_SOURCE_ID);
}

interface AirportInfo {
  ident: string;
  name: string;
  distNm: number;
  hdgMag: number | null;
  runways: Runway[];
  pavedFt: number | null;
}

export function EmergencyGlideButton() {
  const navigate = useNavigate();
  const visible = useLayerVisibilityStore((s) => s.layers.glideRing);
  const emergencyMode = useUiStore((s) => s.glideRingEmergencyMode);
  const setEmergencyMode = useUiStore((s) => s.setGlideRingEmergencyMode);
  const safetyBufferFt = useUiStore((s) => s.glideRingSafetyBufferFt);
  const onGround = useUiStore((s) => s.glideRingOnGround);

  const gpsFix = useGpsStore((s) => s.fix);
  const aircraft = useAircraftStore(selectActiveAircraft);
  const windsProfile = useOwnshipWindsStore((s) => s.profile);

  const [reachableCount, setReachableCount] = useState<number | null>(null);
  const [reachableAirports, setReachableAirports] = useState<BboxAirport[]>([]);
  const [selectedAirport, setSelectedAirport] = useState<AirportInfo | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  // Only render the button when the layer toggle is on AND we have
  // enough state to actually compute a ring.
  const canActivate =
    visible &&
    gpsFix &&
    gpsFix.altMeters != null &&
    aircraft?.glide?.bestGlideKt &&
    aircraft?.glide?.bestGlideRatio;

  // Compute + highlight airports when entering emergency mode.
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    if (!emergencyMode || !canActivate || !gpsFix || gpsFix.altMeters == null) {
      removeHighlightLayers(map);
      setReachableCount(null);
      return;
    }

    inFlight.current?.abort();
    const ac = new AbortController();
    inFlight.current = ac;

    const altFt = gpsFix.altMeters * FT_PER_M;
    const sampler = (lat: number, lon: number) => sampleElevationFt(lat, lon, undefined, ac.signal);

    (async () => {
      // Recompute the ring fresh — we want the latest state for the
      // emergency-mode airport filter (the GlideRingLayer ring may be
      // slightly stale due to its 400ms debounce).
      const ring = await computeGlideRing({
        lat: gpsFix.lat,
        lon: gpsFix.lon,
        altMslFt: altFt,
        bestGlideKt: aircraft!.glide!.bestGlideKt!,
        bestGlideRatio: aircraft!.glide!.bestGlideRatio!,
        safetyBufferFt,
        sampleTerrainFt: sampler,
        windsProfile,
      });
      if (ac.signal.aborted) return;
      if (ring.empty) {
        setReachableCount(0);
        ensureHighlightLayers(map);
        (map.getSource(HIGHLIGHT_SOURCE_ID) as GeoJSONSource | undefined)?.setData(emptyFC());
        return;
      }

      // Fetch airports inside the ring bbox.
      const [w, s, e, n] = ringBbox(ring.ring);
      let airports: BboxAirport[] = [];
      try {
        airports = await fetchBbox({
          layer: 'airports',
          west: w,
          south: s,
          east: e,
          north: n,
          zoom: 8,
          limit: 200,
          signal: ac.signal,
        });
      } catch (err) {
        console.warn('[EmergencyGlideButton] airport fetch failed', err);
      }
      if (ac.signal.aborted) return;

      const inside = airports.filter((a) => pointInRing(a.lon, a.lat, ring.ring));

      // Sort by distance from ownship — closest first.
      const sorted = [...inside].sort((a, b) => {
        const da = Math.hypot(a.lat - gpsFix.lat, (a.lon - gpsFix.lon) * Math.cos(gpsFix.lat * Math.PI / 180));
        const db = Math.hypot(b.lat - gpsFix.lat, (b.lon - gpsFix.lon) * Math.cos(gpsFix.lat * Math.PI / 180));
        return da - db;
      });
      setReachableCount(sorted.length);
      setReachableAirports(sorted);

      ensureHighlightLayers(map);
      const fc: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: sorted.map((a) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
          properties: { ident: a.ident, name: a.name, type: a.type, pavedFt: a.pavedFt ?? 0 },
        })),
      };
      (map.getSource(HIGHLIGHT_SOURCE_ID) as GeoJSONSource | undefined)?.setData(fc);

      // Auto-open closest airport info.
      if (sorted.length > 0 && !ac.signal.aborted) {
        const closest = sorted[0];
        const distNm = Math.hypot(
          (closest.lat - gpsFix.lat) * 60,
          (closest.lon - gpsFix.lon) * 60 * Math.cos(gpsFix.lat * Math.PI / 180)
        );
        const hdgTrue = bearingDeg(gpsFix.lat, gpsFix.lon, closest.lat, closest.lon);
        // Fetch runway detail in background (non-blocking)
        getAirportDetail(closest.ident).then((detail) => {
          if (ac.signal.aborted) return;
          setSelectedAirport({
            ident: closest.ident,
            name: closest.name,
            distNm,
            hdgMag: hdgTrue,  // magnetic correction not applied here — close enough for emergency
            runways: detail?.runways ?? [],
            pavedFt: closest.pavedFt ?? null,
          });
        }).catch(() => {
          if (ac.signal.aborted) return;
          setSelectedAirport({
            ident: closest.ident, name: closest.name, distNm,
            hdgMag: hdgTrue, runways: [], pavedFt: closest.pavedFt ?? null,
          });
        });
      }
    })().catch((err: unknown) => {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.warn('[EmergencyGlideButton] highlight pipeline failed', err);
    });

    return () => {
      ac.abort();
    };
  }, [emergencyMode, canActivate, gpsFix?.lat, gpsFix?.lon, gpsFix?.altMeters, aircraft, windsProfile, safetyBufferFt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click any highlighted airport to open its info box.
  useEffect(() => {
    if (!emergencyMode) return;
    const map = getMapInstance();
    if (!map) return;

    const onClick = (e: MapMouseEvent & { features?: any[] }) => {
      const feat = e.features?.[0];
      if (!feat || !gpsFix) return;
      const { ident, name, pavedFt } = feat.properties as { ident: string; name: string; pavedFt: number };
      const [lon, lat] = (feat.geometry as GeoJSON.Point).coordinates;
      const distNm = Math.hypot(
        (lat - gpsFix.lat) * 60,
        (lon - gpsFix.lon) * 60 * Math.cos(gpsFix.lat * Math.PI / 180)
      );
      const hdgTrue = bearingDeg(gpsFix.lat, gpsFix.lon, lat, lon);
      // Show immediately with basic data, then enrich with runway detail.
      setSelectedAirport({ ident, name, distNm, hdgMag: hdgTrue, runways: [], pavedFt: pavedFt || null });
      getAirportDetail(ident).then((detail) => {
        setSelectedAirport((prev) => prev?.ident === ident
          ? { ...prev, runways: detail?.runways ?? [] } : prev
        );
      }).catch(() => {});
    };

    const onMouseEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const onMouseLeave = () => { map.getCanvas().style.cursor = ''; };

    map.on('click', HIGHLIGHT_LAYER_ID, onClick);
    map.on('mouseenter', HIGHLIGHT_LAYER_ID, onMouseEnter);
    map.on('mouseleave', HIGHLIGHT_LAYER_ID, onMouseLeave);
    return () => {
      map.off('click', HIGHLIGHT_LAYER_ID, onClick);
      map.off('mouseenter', HIGHLIGHT_LAYER_ID, onMouseEnter);
      map.off('mouseleave', HIGHLIGHT_LAYER_ID, onMouseLeave);
    };
  }, [emergencyMode, gpsFix]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear popup when exiting emergency mode.
  useEffect(() => {
    if (!emergencyMode) {
      setSelectedAirport(null);
      setReachableAirports([]);
    }
  }, [emergencyMode]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      inFlight.current?.abort();
      const map = getMapInstance();
      if (map) removeHighlightLayers(map);
    };
  }, []);

  if (!visible) return null;

  // Layer is on but we can't yet compute a ring. Show a status chip so
  // the user knows the layer is active and what's missing.
  if (!canActivate) {
    let reason = 'Waiting for GPS…';
    let actionTo: string | null = null;
    let actionLabel: string | null = null;
    if (gpsFix && gpsFix.altMeters == null) {
      reason = 'No GPS altitude (need 3D fix)';
    } else if (!aircraft) {
      reason = 'Add an aircraft profile';
      actionTo = '/aircraft';
      actionLabel = 'Open Aircraft';
    } else if (!aircraft?.glide?.bestGlideKt) {
      reason = 'Aircraft missing best glide speed';
      actionTo = '/aircraft';
      actionLabel = 'Edit aircraft';
    } else if (!aircraft?.glide?.bestGlideRatio) {
      reason = 'Aircraft missing glide ratio';
      actionTo = '/aircraft';
      actionLabel = 'Edit aircraft';
    }
    return (
      <div
        className="absolute z-20 bottom-[calc(var(--instr-h,56px)+0.75rem)] right-3 pointer-events-auto flex flex-col items-end gap-1.5"
        style={{ maxWidth: '260px' }}
      >
        <div className="text-[11px] px-3 py-2 rounded-lg bg-cockpit-800/90 border border-cockpit-600 text-cockpit-200 shadow-bar text-right backdrop-blur">
          ⚠ Glide ring · {reason}
        </div>
        {actionTo && actionLabel && (
          <button
            onClick={() => navigate(actionTo!)}
            className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-cockpit-700/95 border border-cockpit-500 text-cockpit-50 hover:bg-cockpit-600 shadow-bar backdrop-blur"
          >
            {actionLabel} →
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="absolute z-20 bottom-[calc(var(--instr-h,56px)+0.75rem)] right-3 flex flex-col items-end gap-2 pointer-events-auto"
      style={{ maxWidth: '260px' }}
    >
      {onGround && (
        <div className="text-[11px] px-3 py-2 rounded-lg bg-cockpit-800/90 border border-cockpit-600 text-cockpit-200 shadow-bar text-right backdrop-blur">
          ✈ On ground · glide ring activates in flight
        </div>
      )}
      {/* Airport info popup */}
      {emergencyMode && selectedAirport && (
        <div className="bg-cockpit-900/95 border border-green-500 rounded-xl p-3 shadow-xl backdrop-blur text-left min-w-[200px] max-w-[260px]">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <div className="text-sm font-bold text-white">{selectedAirport.ident}</div>
              <div className="text-[11px] text-cockpit-300 leading-tight">{selectedAirport.name}</div>
            </div>
            <button
              onClick={() => setSelectedAirport(null)}
              className="text-cockpit-400 hover:text-white text-xs shrink-0 mt-0.5"
            >✕</button>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] mb-2">
            <div className="text-cockpit-400">Distance</div>
            <div className="text-white font-mono">{selectedAirport.distNm.toFixed(1)} NM</div>
            <div className="text-cockpit-400">Heading to</div>
            <div className="text-white font-mono">
              {selectedAirport.hdgMag != null ? `${Math.round(selectedAirport.hdgMag).toString().padStart(3,'0')}°M` : '—'}
            </div>
            {selectedAirport.pavedFt != null && selectedAirport.pavedFt > 0 && (
              <>
                <div className="text-cockpit-400">Longest rwy</div>
                <div className="text-white font-mono">{selectedAirport.pavedFt.toLocaleString()}’</div>
              </>
            )}
          </div>
          {selectedAirport.runways.length > 0 && (
            <div className="border-t border-cockpit-700 pt-2">
              <div className="text-[10px] text-cockpit-400 tracking-wider mb-1">RUNWAYS</div>
              {selectedAirport.runways
                .filter((r) => !r.closed && r.length_ft)
                .sort((a, b) => (b.length_ft ?? 0) - (a.length_ft ?? 0))
                .slice(0, 4)
                .map((r, i) => (
                  <div key={i} className="flex justify-between text-[11px] py-0.5">
                    <span className="text-cockpit-200 font-mono">
                      {r.le_ident}/{r.he_ident}
                    </span>
                    <span className="text-white font-mono">
                      {r.length_ft != null ? `${r.length_ft.toLocaleString()}'` : '—'}
                    </span>
                  </div>
                ))
              }
              {selectedAirport.runways.filter(r => !r.closed && r.length_ft).length === 0 && (
                <div className="text-[11px] text-cockpit-400 italic">Loading…</div>
              )}
            </div>
          )}
          {selectedAirport.runways.length === 0 && (
            <div className="text-[11px] text-cockpit-400 italic">Loading runway data…</div>
          )}
        </div>
      )}
      {emergencyMode && reachableCount === 0 && (
        <div className="text-[11px] px-3 py-2 rounded-lg bg-red-700/95 border border-red-400 text-white font-semibold shadow-bar text-right">
          ⚠ NO AIRPORTS IN GLIDE RANGE
        </div>
      )}
      {emergencyMode && typeof reachableCount === 'number' && reachableCount > 0 && (
        <div className="text-[11px] px-3 py-2 rounded-lg bg-green-700/95 border border-green-400 text-white font-semibold shadow-bar text-right">
          {reachableCount} airport{reachableCount === 1 ? '' : 's'} reachable
        </div>
      )}
      <button
        onClick={() => setEmergencyMode(!emergencyMode)}
        disabled={onGround}
        className={[
          'text-[12px] font-semibold px-3 py-2 rounded-lg shadow-bar backdrop-blur border flex items-center gap-1.5',
          onGround
            ? 'bg-cockpit-800/60 border-cockpit-700 text-cockpit-400 cursor-not-allowed'
            : emergencyMode
              ? 'bg-red-600/95 border-red-300 text-white hover:bg-red-500'
              : 'bg-cockpit-800/90 border-cockpit-600 text-cockpit-100 hover:bg-cockpit-700/90',
        ].join(' ')}
        title={
          onGround
            ? 'On ground — glide ring unavailable'
            : emergencyMode
              ? 'Exit emergency glide view'
              : 'Emergency glide — zoom + highlight reachable airports'
        }
      >
        {emergencyMode ? '✕ Exit emergency' : '⚠ Emergency glide'}
      </button>
    </div>
  );
}
